import { useCallback, useMemo } from 'react';
import { atom, useAtom } from 'jotai';
import type { ColorPaletteName } from '../../constants/color';
import { useMapVisibility } from './useMapVisibility';
import { useLegendConfig } from './useLegendConfig';
import { useMapColor } from './useMapColor';
import { useMapColorPalette } from './useMapColorPalette';

type AggregationTarget = (typeof AGGREGATION_TARGETS)[number];

type AggregationSubTarget = (typeof AGGREGATION_SUB_TARGETS)[number];

type AggregationType = (typeof AGGREGATION_TYPES)[number];

type AggregationMetric = (typeof AGGREGATION_METRICS)[number];

type AggregationConfig = Readonly<{
  target: AggregationTarget;
  subTarget?: AggregationSubTarget | undefined;
  metric?: AggregationMetric | undefined;
  type: AggregationType;
}>;

type UseAggregationConfig = {
  aggregationConfig: AggregationConfig | null;
  availableAggregationTypes: AggregationType[];
  startAggregation: (
    target: AggregationTarget,
    subTarget?: AggregationSubTarget | undefined,
    type?: AggregationType | undefined,
    metric?: AggregationMetric | undefined
  ) => void;
  clearAggregation: () => void;
  changeAggregationSubTarget: (subTarget: AggregationSubTarget) => void;
  changeAggregationMetric: (metric: AggregationMetric) => void;
  changeAggregationType: (type: AggregationType) => void;
};

const AGGREGATION_TARGETS = [
  'traveled_distance',
  'traveled_time',
  'activity_time',
  'home',
  'work',
  'start',
  'end',
  'trip_path',
  'stop_by',
  'od_map',
] as const;

const AGGREGATION_SUB_TARGETS = ['home', 'work', 'start', 'end'] as const;

const AGGREGATION_METRICS = [
  'count_per_area',
  'count',
  'average_traveled_distance',
  'average_traveled_time',
  'average_activity_time',
] as const;

const AGGREGATION_TYPES = [
  'areas',
  'area_pref',
  'heatmap',
  'grid_heatmap',
  'arcs',
  'od_map',
  'links',
] as const;

const DEFAULT_COLOR_PALETTES = {
  areas: 'vivid',
  area_pref: 'vivid',
  grid_heatmap: 'vivid',
  heatmap: 'vivid',
  arcs: 'cool',
  links: 'colorful',
  od_map: 'vivid',
} as const satisfies Record<AggregationType, ColorPaletteName>;

const getAvailableTypes = (
  target: AggregationTarget,
  subTarget?: AggregationSubTarget | undefined
): AggregationType[] => {
  // それぞれの可視化対象に応じて利用可能なモード一覧を返す
  switch (target) {
    case 'start':
    case 'end':
    case 'work':
      return ['areas', 'grid_heatmap', 'heatmap'];
    case 'home':
      return ['areas', 'area_pref', 'grid_heatmap', 'heatmap'];
    case 'traveled_time':
    case 'traveled_distance':
    case 'activity_time':
      if (subTarget === 'home' || subTarget === 'work') {
        return ['areas'];
      }
      return ['areas', 'grid_heatmap'];
    case 'trip_path':
      return ['arcs', 'links'];
    case 'stop_by':
      return ['grid_heatmap', 'heatmap'];
    case 'od_map':
      return ['od_map'];
  }
};

const getAvailableMetrics = (
  target: AggregationTarget
): AggregationMetric[] => {
  // それぞれの可視化対象に応じて利用可能な集計方法一覧を返す
  switch (target) {
    case 'home':
    case 'work':
    case 'start':
    case 'end':
      return ['count_per_area', 'count'];
    case 'traveled_distance':
    case 'traveled_time':
    case 'activity_time':
      return [`average_${target}`];
    case 'trip_path':
    case 'stop_by':
    case 'od_map':
      return [];
  }
};

const aggregationConfigAtom = atom<AggregationConfig | null>(null);

const useAggregationConfig = (): UseAggregationConfig => {
  const [aggregationConfig, setAggregationConfig] = useAtom(
    aggregationConfigAtom
  );

  const {
    visibleAreaType,
    visibleLinkTypes,
    setVisibleAreaType,
    setVisibleLinkTypes,
    setIsAggregationVisible,
  } = useMapVisibility();
  const { mapColor } = useMapColor();
  const { setMapColorPalette, setMapColorOpacity } = useMapColorPalette();
  const { resetLegendConfig } = useLegendConfig();

  const availableAggregationTypes = useMemo((): AggregationType[] => {
    if (!aggregationConfig) {
      return [];
    }
    return getAvailableTypes(
      aggregationConfig.target,
      aggregationConfig.subTarget
    );
  }, [aggregationConfig]);

  const startAggregation = useCallback(
    (
      target: AggregationTarget,
      subTarget?: AggregationSubTarget | undefined,
      type?: AggregationType | undefined,
      metric?: AggregationMetric | undefined
    ): void => {
      // サブターゲットが必要な場合はデフォルト値を設定する
      const newSubTarget = [
        'activity_time',
        'traveled_time',
        'traveled_distance',
      ].includes(target)
        ? (subTarget ?? aggregationConfig?.subTarget ?? 'end')
        : undefined;

      // 可視化モードが未指定の場合は現在のモードを引き継ぎ、それもない場合は利用可能な最初のモードに切り替える
      const availableTypes = getAvailableTypes(target, newSubTarget);
      const newType =
        [type, aggregationConfig?.type].find(
          (m: AggregationType | undefined): boolean => {
            return !!m && availableTypes.includes(m);
          }
        ) ?? availableTypes[0];

      // 集計方法も同様に、未指定の場合は現在の集計方法を引き継ぎ、それもない場合は利用可能な最初の集計方法に切り替える
      const availableMetrics = getAvailableMetrics(target);
      const newMetric =
        [metric, aggregationConfig?.metric].find(
          (m: AggregationMetric | undefined): boolean => {
            return !!m && availableMetrics.includes(m);
          }
        ) ?? availableMetrics[0];

      setAggregationConfig({
        target: target,
        subTarget: newSubTarget,
        metric: newMetric,
        type: newType,
      });

      // 可視化対象に応じて初期の表示設定を行う
      if (newType === 'areas' && !visibleAreaType) {
        setVisibleAreaType('town');
      }
      if (newType === 'links' && visibleLinkTypes.length === 0) {
        setVisibleLinkTypes(['highway', 'railway']);
      }
      setIsAggregationVisible(true);

      // 可視化モードが変わった場合はカラーパレットを初期化する
      if (newType !== aggregationConfig?.type) {
        setMapColorPalette(DEFAULT_COLOR_PALETTES[newType]);
        setMapColorOpacity(mapColor === 'light' ? 0.5 : 0.7);
      }

      // 凡例設定を初期化する
      resetLegendConfig();
    },
    [
      aggregationConfig,
      visibleAreaType,
      visibleLinkTypes,
      mapColor,
      setAggregationConfig,
      setVisibleAreaType,
      setVisibleLinkTypes,
      setIsAggregationVisible,
      setMapColorPalette,
      setMapColorOpacity,
      resetLegendConfig,
    ]
  );

  const clearAggregation = useCallback((): void => {
    setAggregationConfig(null);
  }, [setAggregationConfig]);

  const changeAggregationSubTarget = useCallback(
    (subTarget: AggregationSubTarget): void => {
      if (!aggregationConfig) {
        return;
      }
      const availableTypes = getAvailableTypes(
        aggregationConfig.target,
        subTarget
      );

      if (availableTypes.includes(aggregationConfig.type)) {
        setAggregationConfig({ ...aggregationConfig, subTarget: subTarget });
      } else {
        // サブターゲットの変更により現在の可視化モードが利用できなくなった場合、優先度の高いものにフォールバックする
        setAggregationConfig({
          ...aggregationConfig,
          subTarget: subTarget,
          type: availableTypes[0],
        });
        setMapColorPalette(DEFAULT_COLOR_PALETTES[availableTypes[0]]);
        setMapColorOpacity(mapColor === 'light' ? 0.5 : 0.7);
      }

      // 可視化対象が変わったため凡例設定を初期化する
      resetLegendConfig();
    },
    [
      aggregationConfig,
      mapColor,
      setAggregationConfig,
      setMapColorPalette,
      setMapColorOpacity,
      resetLegendConfig,
    ]
  );

  const changeAggregationMetric = useCallback(
    (metric: AggregationMetric): void => {
      if (!aggregationConfig) {
        return;
      }
      if (!getAvailableMetrics(aggregationConfig.target).includes(metric)) {
        return;
      }

      setAggregationConfig({ ...aggregationConfig, metric: metric });

      // 値の尺度が変わったため凡例設定を初期化する
      resetLegendConfig();
    },
    [aggregationConfig, setAggregationConfig, resetLegendConfig]
  );

  const changeAggregationType = useCallback(
    (type: AggregationType): void => {
      if (!aggregationConfig) {
        return;
      }
      if (
        !getAvailableTypes(
          aggregationConfig.target,
          aggregationConfig.subTarget
        ).includes(type)
      ) {
        return;
      }

      setAggregationConfig({ ...aggregationConfig, type: type });

      // 可視化モードが変わったためカラーパレットを初期化する
      setMapColorPalette(DEFAULT_COLOR_PALETTES[type]);
      setMapColorOpacity(mapColor === 'light' ? 0.5 : 0.7);

      // 可視化モードが変わったため凡例設定を初期化する
      resetLegendConfig();
    },
    [
      aggregationConfig,
      mapColor,
      setAggregationConfig,
      setMapColorPalette,
      setMapColorOpacity,
      resetLegendConfig,
    ]
  );

  return {
    aggregationConfig,
    availableAggregationTypes,
    startAggregation,
    clearAggregation,
    changeAggregationSubTarget,
    changeAggregationMetric,
    changeAggregationType,
  };
};

export type {
  AggregationTarget,
  AggregationSubTarget,
  AggregationMetric,
  AggregationType,
};
export {
  useAggregationConfig,
  AGGREGATION_TARGETS,
  AGGREGATION_SUB_TARGETS,
  AGGREGATION_METRICS,
  AGGREGATION_TYPES,
};
